import { Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionEyebrow from "@/components/SectionEyebrow";
import Seo from "@/components/Seo";
import FAQ from "@/components/FAQ";
import { seoPages } from "@/data/seoPages";
import { serviceJsonLd, faqJsonLd, breadcrumbJsonLd } from "@/lib/seo";

export default function SeoLanding({ slug }) {
    const page = seoPages.find((p) => p.slug === slug);
    if (!page) return <Navigate to="/" replace />;

    const cta = { label: "Book Your Consultation — ₹1,999", to: "/consultation" };
    const crumbName = page.eyebrow.split("·")[0].trim();

    return (
        <div data-testid={`seo-page-${page.slug}`} className="relative">
            <Seo
                title={page.title}
                description={page.metaDescription}
                path={`/${page.slug}`}
                jsonLd={[
                    serviceJsonLd({
                        name: page.service.name,
                        description: page.metaDescription,
                        price: page.service.price,
                        url: `/${page.slug}`,
                    }),
                    faqJsonLd(page.faqs),
                    breadcrumbJsonLd([
                        { name: "Home", path: "/" },
                        { name: crumbName, path: `/${page.slug}` },
                    ]),
                ]}
            />

            {/* BREADCRUMB */}
            <nav aria-label="Breadcrumb" data-testid="seo-breadcrumb" className="mx-auto max-w-7xl px-6 pt-8 lg:px-10">
                <ol className="flex flex-wrap items-center gap-1.5 text-xs text-white/45">
                    <li><Link to="/" className="transition hover:text-gold-300">Home</Link></li>
                    <li className="flex items-center gap-1.5"><ChevronRight size={12} /> <span className="text-white/70">{crumbName}</span></li>
                </ol>
            </nav>

            {/* HERO */}
            <section className="mx-auto max-w-7xl px-6 pt-10 pb-14 lg:px-10">
                <Reveal><SectionEyebrow>{page.eyebrow}</SectionEyebrow></Reveal>
                <Reveal delay={0.1}>
                    <h1 className="mt-6 max-w-4xl font-display text-4xl font-semibold leading-[1.08] tracking-tighter sm:text-5xl lg:text-6xl">
                        {page.h1Pre}{" "}
                        <span className="gold-text">{page.h1Gold}</span>
                    </h1>
                </Reveal>
                {page.intro.map((p, i) => (
                    <Reveal key={i} delay={0.15 + i * 0.05}>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/65 md:text-lg">{p}</p>
                    </Reveal>
                ))}
                <Reveal delay={0.3}>
                    <div className="mt-9 flex flex-wrap items-center gap-4">
                        <Link to={cta.to} data-testid="seo-page-cta" className="gold-button inline-flex items-center gap-2">
                            {cta.label} <ArrowRight size={14} />
                        </Link>
                        <Link to="/contact" className="ghost-button inline-flex items-center gap-2">
                            Contact Me
                        </Link>
                    </div>
                </Reveal>
            </section>

            {/* SECTIONS */}
            {page.sections.map((s, i) => (
                <section key={s.h2} className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
                    <Reveal>
                        <div className="glass glow-border rounded-lg p-8 md:p-12">
                            <h2 className="font-display text-2xl font-medium tracking-tight sm:text-3xl">
                                {s.h2}
                            </h2>
                            {s.body && (
                                <p className="mt-5 max-w-3xl text-base leading-relaxed text-white/65 md:text-lg">{s.body}</p>
                            )}
                            {s.bullets && (
                                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                                    {s.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2.5 text-sm text-white/75 md:text-base">
                                            <Check size={14} className="mt-1 flex-none text-gold-300" /><span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </Reveal>
                </section>
            ))}

            <FAQ items={page.faqs} eyebrow="FAQ" title={`${crumbName} —`} gold="common questions." />

            {/* RELATED LINKS */}
            <section className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
                <Reveal>
                    <p className="label-eyebrow">Keep Exploring</p>
                </Reveal>
                <div className="mt-6 flex flex-wrap gap-3">
                    {page.related.map((r) => (
                        <Reveal key={r.to + r.label}>
                            <Link to={r.to} className="glass glass-hover inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-white/80 transition hover:text-gold-300">
                                {r.label} <ArrowRight size={12} />
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
                <Reveal>
                    <div className="glass glow-border relative overflow-hidden rounded-lg p-10 text-center md:p-14">
                        <span className="pointer-events-none absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.4), transparent 60%)" }} />
                        <div className="relative">
                            <h2 className="mx-auto max-w-2xl font-display text-3xl font-medium leading-tight sm:text-4xl">
                                Ready to see your business{" "}
                                <span className="gold-text">clearly?</span>
                            </h2>
                            <p className="mx-auto mt-4 max-w-xl text-sm text-white/60 md:text-base">
                                1-Hour Business Consultation ₹1,999 · Business growth workshops for teams
                            </p>
                            <Link to={cta.to} data-testid="seo-page-final-cta" className="gold-button mt-8 inline-flex items-center gap-2">
                                {cta.label} <ArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
